import type {
  UploadUrlRequest,
  UploadUrlResponse,
  FileAnalysisResponse,
  SheetSelectionResponse,
  FileInfo,
  UniqueValuesResponse,
  ValidationResponse,
  MappingConfig,
  CreateJobRequest,
  CreateJobResponse,
  JobStatusResponse,
  SavedConfig,
} from '../utils/types';
import { getBearerToken } from 'application/common/utils/authUtils';

const LEAD_IMPORT_BASE =
  'https://8qj6ef4ggl.execute-api.us-west-2.amazonaws.com/prod/lead-import';

export class LeadImportApiError extends Error {
  constructor(public status: number, public body: any, message: string) {
    super(message);
    this.name = 'LeadImportApiError';
  }
}

export class LeadImportService {
  private async getAuthToken(): Promise<string | null> {
    return getBearerToken();
  }

  private async buildHeaders(
    extra?: Record<string, string>
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = await this.getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return { ...headers, ...(extra || {}) };
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = await this.buildHeaders(
      options.headers as Record<string, string>
    );
    const res = await fetch(`${LEAD_IMPORT_BASE}${path}`, {
      ...options,
      headers,
    });

    let body: any;
    try {
      body = await res.json();
    } catch {
      body = null;
    }

    if (!res.ok) {
      throw new LeadImportApiError(
        res.status,
        body,
        `Lead Import API error: ${res.status}`
      );
    }

    return body as T;
  }

  async getUploadUrl(req: UploadUrlRequest): Promise<UploadUrlResponse> {
    const raw: any = await this.request('/files/upload-url', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    // Normalize response - backend may use fileId, file_id, or id
    return {
      uploadUrl:
        raw.uploadUrl || raw.upload_url || raw.presignedUrl || raw.url || '',
      fileID: raw.fileID || raw.fileId || raw.file_id || raw.id || '',
    };
  }

  async analyzeFile(fileID: string): Promise<FileAnalysisResponse> {
    const raw: any = await this.request(`/files/${fileID}/analyze`, {
      method: 'POST',
    });
    // Normalize API response field names to match our interface
    return {
      status: raw.status,
      headers: raw.headers,
      rowCount: raw.rowCount ?? raw.row_count,
      sampleData:
        raw.sampleData ?? raw.sampleRows ?? raw.sample_data ?? raw.sample_rows,
      sheets: raw.sheets,
    };
  }

  async selectSheet(
    fileID: string,
    sheetName: string
  ): Promise<SheetSelectionResponse> {
    return this.request<SheetSelectionResponse>(
      `/files/${fileID}/select-sheet`,
      {
        method: 'POST',
        body: JSON.stringify({ sheetName }),
      }
    );
  }

  async listFiles(): Promise<FileInfo[]> {
    const response = await this.request<FileInfo[] | { files: FileInfo[] }>(
      '/files',
      {
        method: 'GET',
      }
    );
    return Array.isArray(response) ? response : response.files ?? [];
  }

  async getFile(fileID: string): Promise<FileInfo> {
    return this.request<FileInfo>(`/files/${fileID}`, {
      method: 'GET',
    });
  }

  async getUniqueValues(
    fileID: string,
    columnName: string
  ): Promise<UniqueValuesResponse> {
    return this.request<UniqueValuesResponse>(
      `/files/${fileID}/columns/${encodeURIComponent(
        columnName
      )}/unique-values`,
      { method: 'POST' }
    );
  }

  async validateMapping(
    fileID: string,
    config: MappingConfig
  ): Promise<ValidationResponse> {
    return this.request<ValidationResponse>(`/files/${fileID}/validate`, {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async createJob(req: CreateJobRequest): Promise<CreateJobResponse> {
    // Backend expects the mapping config flattened at the top level
    const config = req.mappingConfig;

    // Transform flexAttributes: convert dot-notation keys (e.g. "contactInformation.firstName")
    // into nested objects (e.g. { contactInformation: { firstName: "column" } })
    const rawFlex = config.columnMappings?.flexAttributes ?? {};
    const transformedFlex: Record<string, any> = {};
    for (const [key, value] of Object.entries(rawFlex)) {
      if (key.includes('.')) {
        const [parent, child] = key.split('.', 2);
        if (!transformedFlex[parent]) transformedFlex[parent] = {};
        transformedFlex[parent][child] = value;
      } else {
        transformedFlex[key] = value;
      }
    }

    const columnMappings = {
      ...config.columnMappings,
      flexAttributes:
        Object.keys(transformedFlex).length > 0 ? transformedFlex : undefined,
    };

    const payload: Record<string, any> = {
      fileId: req.fileID,
      importMode: config.importMode,
      columnMappings,
      ownerStrategy: config.ownerStrategy,
      stageStrategy: config.stageStrategy,
      dispositionStrategy: config.dispositionStrategy,
      categoryID: config.categoryID,
    };

    // Add dealMappingsName and dealMappingsDescription if provided
    if (req.dealMappingsName) {
      payload.dealMappingsName = req.dealMappingsName;
    }
    if (req.dealMappingsDescription) {
      payload.dealMappingsDescription = req.dealMappingsDescription;
    }

    // Save config option
    if (req.saveConfig) {
      payload.saveConfig = true;
      payload.configName = req.saveConfig.name;
    }

    const raw: any = await this.request('/jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return { jobID: raw.jobID || raw.jobId || raw.job_id || raw.id || '' };
  }

  async getJobStatus(jobID: string): Promise<JobStatusResponse> {
    return this.request<JobStatusResponse>(`/jobs/${jobID}`, {
      method: 'GET',
    });
  }

  async listConfigs(): Promise<SavedConfig[]> {
    const response = await this.request<
      SavedConfig[] | { configs: SavedConfig[] }
    >('/configs', {
      method: 'GET',
    });
    return Array.isArray(response) ? response : response.configs ?? [];
  }

  async saveConfig(name: string, config: MappingConfig): Promise<SavedConfig> {
    return this.request<SavedConfig>('/configs', {
      method: 'POST',
      body: JSON.stringify({ name, config }),
    });
  }
}

export const leadImportService = new LeadImportService();

export default leadImportService;
